import { CSSProperties, HTMLAttributes } from 'react'
import { KTIcon } from '../../../_metronic/helpers'
import { ILedgerBalanceSummary } from './core/types'

const labelClass = 'fw-bold text-gray-500 fs-8 text-uppercase lh-1'
const divider: CSSProperties = {
  width: 1,
  alignSelf: 'stretch',
  backgroundColor: 'var(--bs-border-color)',
}

function Tile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className='d-flex flex-column'>
      <span className={labelClass}>{label}</span>
      <span
        className={
          accent
            ? 'fw-bolder fs-6 text-gray-900 lh-1 mt-1'
            : 'fw-semibold fs-7 text-gray-700 lh-1 mt-1'
        }
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </span>
    </div>
  )
}

interface LedgerBalanceClusterProps extends HTMLAttributes<HTMLDivElement> {
  summary: ILedgerBalanceSummary[]
  hasBlocked: boolean
  entityCount: number
}

/**
 * Header widget body — adaptive by data shape (CPAYMENT-1600):
 *
 * - ONE currency (single wallet, or several summing into one currency): the full
 *   inline trio — the at-a-glance value the ticket asks for.
 * - SEVERAL currencies: there is no "primary" currency (each merchant holds its
 *   own), and money across currencies cannot be summed. Showing one currency
 *   would hide the others and showing them all would grow the navbar per currency.
 *   So it collapses to a fixed-width trigger and the popover carries every
 *   currency and merchant.
 *
 * Either way the navbar stays exactly one row of constant height.
 */
export function LedgerBalanceCluster({
  summary,
  hasBlocked,
  entityCount,
  ...rest
}: LedgerBalanceClusterProps) {
  const single = summary.length === 1 ? summary[0] : null
  const multiCurrency = summary.length > 1
  const expandable = entityCount > 1 || multiCurrency

  if (summary.length === 0) {
    return null
  }

  return (
    // `rest` carries the mouse/focus handlers SmartTooltip injects via cloneElement —
    // they must land on a real DOM node, otherwise the popover never opens.
    <div
      {...rest}
      data-testid='ledger-header-balance'
      className='d-flex align-items-center gap-3 px-3 py-2 rounded'
      style={{
        border: '1px solid var(--bs-border-color)',
        backgroundColor: 'var(--bs-body-bg)',
        cursor: expandable ? 'pointer' : 'default',
      }}
    >
      {single ? (
        <>
          <Tile label='Доступно' value={`${single.availableBalance} ${single.currency}`} accent />
          <div style={divider} />
          <Tile label='Кредит' value={single.creditBalance} />
          <div style={divider} />
          <Tile label='Холд' value={single.debitsPending} />
        </>
      ) : (
        <span className='d-flex align-items-center gap-2'>
          <KTIcon iconName='wallet' className='fs-4 text-gray-600' />
          <span className='fw-semibold fs-7 text-gray-800'>Баланс</span>
        </span>
      )}

      {hasBlocked && <span className='badge badge-light-danger fs-8'>блок</span>}

      {expandable && (
        <span className='d-flex align-items-center text-gray-500'>
          <span className='badge badge-circle badge-light fs-8'>{entityCount}</span>
          <KTIcon iconName='arrow-down' className='fs-7 ms-1' />
        </span>
      )}
    </div>
  )
}
